/**
 * OS-level supervision for the agt manager daemon (ENG-4593).
 *
 * Hands manager lifecycle off to the OS so a missing process is the OS's
 * problem, not ours. Replaces the in-process `agt manager start
 * --supervise` wrapper from ENG-4488.
 *
 *   macOS:  launchd LaunchAgent (KeepAlive=true + ThrottleInterval=10)
 *   Linux:  systemd --user unit (Restart=always + RestartSec=5)  [Phase 2]
 *           systemd system unit (Restart=on-failure, root)        [Phase 3 / ENG-4706]
 *
 * Public surface:
 *   - installSupervisor(opts)   — write the unit, load/enable it, verify
 *   - uninstallSupervisor()     — unload/disable + remove the unit (idempotent)
 *   - supervisorStatus()        — quick readout of whether the OS knows about us
 *
 *   - installSystemUnit(opts)   — Linux only, root-only. Writes
 *                                 /etc/systemd/system/agt-manager.service
 *                                 so the manager survives EC2 reboot
 *                                 without `loginctl enable-linger`
 *                                 gymnastics. Used by host-bootstrap and
 *                                 the SSM backfill runbook (ENG-4706).
 *   - uninstallSystemUnit()
 *   - systemUnitStatus()
 *
 * Each platform implementation lives in its own helper. The dispatcher
 * picks one based on `process.platform` and rejects unsupported OSes
 * with a clear error rather than silently no-oping.
 */

import { execFileSync } from 'node:child_process';
import {
  existsSync,
  mkdirSync,
  readFileSync,
  unlinkSync,
  writeFileSync,
  chmodSync,
} from 'node:fs';
import { homedir, platform } from 'node:os';
import { dirname, join } from 'node:path';

// macOS LaunchAgent label and on-disk path. Stable so install/uninstall
// can find each other without a state file.
const MACOS_LABEL = 'team.augmented.agt-manager';
const MACOS_PLIST = join(homedir(), 'Library', 'LaunchAgents', `${MACOS_LABEL}.plist`);

// Linux systemd --user unit. Lives under the operator's XDG config so it
// survives reboot only after `loginctl enable-linger` (we surface that as
// a follow-up step rather than running it ourselves — needs root).
const LINUX_UNIT_NAME = 'agt-manager.service';
const LINUX_UNIT_PATH = join(homedir(), '.config', 'systemd', 'user', LINUX_UNIT_NAME);
// Secrets live in a separate EnvironmentFile (mode 0600) instead of
// being inlined into the unit body. The unit file itself is still
// 0600 but plain Environment= lines in it are visible to anything
// that can read systemd's parsed unit cache (`systemctl show`,
// `systemd-cgls --all`, etc.). EnvironmentFile content stays private
// to the systemd manager process.
const LINUX_ENV_FILE_PATH = join(homedir(), '.config', 'systemd', 'user', 'agt-manager.env');

export interface InstallOpts {
  /** Absolute path to the agt binary the supervisor should launch. */
  agtBin: string;
  /** Poll interval passed to `agt manager start`. Min 5 seconds. */
  intervalSec: number;
  /** Manager config directory (where pid/log/state files live). */
  configDir: string;
  /**
   * Environment variables the supervised manager needs at launch time.
   * AGT_HOST and AGT_API_KEY are required for the manager to call the
   * API; AGT_TEAM is optional but typical.
   */
  env: Record<string, string>;
}

export type SupervisorPresence =
  | { kind: 'installed'; pid: number | null; details: string }
  | { kind: 'not-installed' }
  | { kind: 'unsupported-platform'; platform: string };

// ---------------------------------------------------------------------------
// Public dispatcher
// ---------------------------------------------------------------------------

export async function installSupervisor(opts: InstallOpts): Promise<{ ok: true; details: string } | { ok: false; error: string }> {
  switch (platform()) {
    case 'darwin':
      return installMacos(opts);
    case 'linux':
      return installLinux(opts);
    default:
      return { ok: false, error: `Unsupported platform: ${platform()}. Supervisor install is macOS / Linux only.` };
  }
}

export async function uninstallSupervisor(): Promise<{ ok: true; details: string } | { ok: false; error: string }> {
  switch (platform()) {
    case 'darwin':
      return uninstallMacos();
    case 'linux':
      return uninstallLinux();
    default:
      return { ok: false, error: `Unsupported platform: ${platform()}. Supervisor uninstall is macOS / Linux only.` };
  }
}

export function supervisorStatus(): SupervisorPresence {
  switch (platform()) {
    case 'darwin':
      return statusMacos();
    case 'linux':
      return statusLinux();
    default:
      return { kind: 'unsupported-platform', platform: platform() };
  }
}

// ---------------------------------------------------------------------------
// macOS — launchd LaunchAgent
// ---------------------------------------------------------------------------

function installMacos(opts: InstallOpts): { ok: true; details: string } | { ok: false; error: string } {
  if (!existsSync(opts.agtBin)) {
    return { ok: false, error: `agt binary not found at ${opts.agtBin}` };
  }
  if (opts.intervalSec < 5) {
    return { ok: false, error: 'intervalSec must be >= 5' };
  }
  if (!opts.env.AGT_HOST || !opts.env.AGT_API_KEY) {
    return { ok: false, error: 'AGT_HOST and AGT_API_KEY must be set in the calling shell so the supervisor can pass them to the manager. Run `agt setup <token>` first.' };
  }

  // If a previous version is already loaded, unload it before rewriting
  // the plist — otherwise launchctl bootstrap will fail with EALREADY.
  bestEffort(() => execFileSync('launchctl', ['unload', '-w', MACOS_PLIST], { stdio: 'ignore' }));

  // Ensure the LaunchAgents directory exists. macOS creates it on first
  // login, but freshly-installed admin users may not have it yet.
  mkdirSync(dirname(MACOS_PLIST), { recursive: true });
  // 0o600 — plist embeds AGT_API_KEY in EnvironmentVariables. World-
  // readable would expose the host's tlk_ token to every local user.
  writeFileSync(MACOS_PLIST, renderMacosPlist(opts), { mode: 0o600 });
  chmodSync(MACOS_PLIST, 0o600);

  try {
    execFileSync('launchctl', ['load', '-w', MACOS_PLIST], { stdio: 'pipe' });
  } catch (err) {
    return { ok: false, error: `launchctl load failed: ${(err as Error).message}` };
  }

  // Verify launchd actually picked it up. `list <label>` returns 0 with
  // a plist of the loaded job's metadata; non-zero if not loaded.
  try {
    const out = execFileSync('launchctl', ['list', MACOS_LABEL], { encoding: 'utf-8' });
    return {
      ok: true,
      details: `Loaded launchd LaunchAgent ${MACOS_LABEL}. Logs: ${join(opts.configDir, 'manager.log')}.\n${out.trim().split('\n').slice(0, 10).join('\n')}`,
    };
  } catch (err) {
    return { ok: false, error: `Plist written but launchctl list reported nothing for ${MACOS_LABEL}: ${(err as Error).message}` };
  }
}

function uninstallMacos(): { ok: true; details: string } | { ok: false; error: string } {
  // Both steps are idempotent — unload reports an error if nothing's
  // loaded, removing the file errors if it doesn't exist. Swallow both;
  // the desired end state is "no LaunchAgent for this label".
  bestEffort(() => execFileSync('launchctl', ['unload', '-w', MACOS_PLIST], { stdio: 'ignore' }));
  let removed = false;
  if (existsSync(MACOS_PLIST)) {
    try {
      unlinkSync(MACOS_PLIST);
      removed = true;
    } catch (err) {
      return { ok: false, error: `Failed to remove ${MACOS_PLIST}: ${(err as Error).message}` };
    }
  }
  return {
    ok: true,
    details: removed
      ? `Removed ${MACOS_LABEL} from launchd and deleted ${MACOS_PLIST}.`
      : `${MACOS_LABEL} was already uninstalled (no plist at ${MACOS_PLIST}).`,
  };
}

function statusMacos(): SupervisorPresence {
  if (!existsSync(MACOS_PLIST)) return { kind: 'not-installed' };
  // launchctl list emits a 3-column line: PID  Status  Label. PID is "-"
  // when the job is loaded but not currently running.
  try {
    const out = execFileSync('launchctl', ['list', MACOS_LABEL], { encoding: 'utf-8' });
    const pidMatch = /"PID"\s*=\s*(\d+);/.exec(out);
    const pid = pidMatch ? Number(pidMatch[1]) : null;
    return { kind: 'installed', pid, details: `Plist: ${MACOS_PLIST}` };
  } catch {
    // File exists but launchctl can't find the label → user removed it
    // out-of-band. Surface as "not installed" so re-install just works.
    return { kind: 'not-installed' };
  }
}

function renderMacosPlist(opts: InstallOpts): string {
  // Load the operator's env into the plist so the manager has the same
  // AGT_HOST / AGT_API_KEY / AGT_TEAM the shell session does. launchd
  // does NOT inherit env from the user's shell — anything we don't bake
  // in here won't be visible to the process.
  const envEntries = Object.entries(opts.env)
    .filter(([k, v]) => k.length > 0 && v != null)
    .map(([k, v]) => `      <key>${escapeXml(k)}</key>\n      <string>${escapeXml(v)}</string>`)
    .join('\n');

  const args = [
    'manager',
    'start',
    '--interval',
    String(opts.intervalSec),
    '--config-dir',
    opts.configDir,
  ];
  const argsXml = args.map((a) => `    <string>${escapeXml(a)}</string>`).join('\n');

  const logPath = join(opts.configDir, 'manager.log');

  return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE plist PUBLIC "-//Apple//DTD PLIST 1.0//EN" "http://www.apple.com/DTDs/PropertyList-1.0.dtd">
<plist version="1.0">
<dict>
  <key>Label</key>
  <string>${MACOS_LABEL}</string>

  <key>ProgramArguments</key>
  <array>
    <string>${escapeXml(opts.agtBin)}</string>
${argsXml}
  </array>

  <key>RunAtLoad</key>
  <true/>

  <key>KeepAlive</key>
  <true/>

  <!-- Throttle restarts so a crashloop doesn't peg the CPU. 10 seconds
       between launches is enough room for transient API blips while
       still recovering quickly from a real crash. -->
  <key>ThrottleInterval</key>
  <integer>10</integer>

  <!-- Where the manager's stdout / stderr land. The manager's logger
       redirects stderr into manager.log itself, but launchd needs an
       explicit destination at the OS level for any pre-logger output
       (e.g., Node startup errors before the watchdog wires up). -->
  <key>StandardOutPath</key>
  <string>${escapeXml(logPath)}</string>
  <key>StandardErrorPath</key>
  <string>${escapeXml(logPath)}</string>

  <key>WorkingDirectory</key>
  <string>${escapeXml(opts.configDir)}</string>

  <key>EnvironmentVariables</key>
  <dict>
${envEntries}
  </dict>
</dict>
</plist>
`;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function bestEffort(fn: () => void): void {
  try { fn(); } catch { /* swallow */ }
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

// ---------------------------------------------------------------------------
// Linux — systemd --user unit
// ---------------------------------------------------------------------------

function installLinux(opts: InstallOpts): { ok: true; details: string } | { ok: false; error: string } {
  if (!existsSync(opts.agtBin)) {
    return { ok: false, error: `agt binary not found at ${opts.agtBin}` };
  }
  if (opts.intervalSec < 5) {
    return { ok: false, error: 'intervalSec must be >= 5' };
  }
  if (!opts.env.AGT_HOST || !opts.env.AGT_API_KEY) {
    return { ok: false, error: 'AGT_HOST and AGT_API_KEY must be set in the calling shell so the supervisor can pass them to the unit. Run `agt setup <token>` first.' };
  }

  // Stop any previous instance before rewriting the unit — saves a
  // restart loop while daemon-reload picks up the new file.
  bestEffort(() => execFileSync('systemctl', ['--user', 'stop', LINUX_UNIT_NAME], { stdio: 'ignore' }));

  // ~/.config/systemd/user/ is the canonical user-unit path. Create it
  // recursively in case this is a fresh user with no XDG layout yet.
  mkdirSync(dirname(LINUX_UNIT_PATH), { recursive: true, mode: 0o700 });
  // Write the secrets-only env file FIRST (mode 0600) so the unit can
  // reference it via EnvironmentFile=. Non-secret env stays in the
  // unit body via Environment= lines for visibility.
  writeFileSync(LINUX_ENV_FILE_PATH, renderLinuxEnvFile(opts), { mode: 0o600 });
  chmodSync(LINUX_ENV_FILE_PATH, 0o600);
  writeFileSync(LINUX_UNIT_PATH, renderLinuxUnit(opts), { mode: 0o600 });
  chmodSync(LINUX_UNIT_PATH, 0o600);

  try {
    execFileSync('systemctl', ['--user', 'daemon-reload'], { stdio: 'pipe' });
  } catch (err) {
    return { ok: false, error: `systemctl --user daemon-reload failed: ${(err as Error).message}` };
  }

  // `enable --now` enables the unit at boot AND starts it immediately.
  // Surviving reboot on a headless host additionally requires
  // `loginctl enable-linger <user>` (root-only); we surface that as a
  // post-install hint rather than running it ourselves.
  try {
    execFileSync('systemctl', ['--user', 'enable', '--now', LINUX_UNIT_NAME], { stdio: 'pipe' });
  } catch (err) {
    return { ok: false, error: `systemctl --user enable --now failed: ${(err as Error).message}. (Hint: 'systemctl --user' requires a per-user dbus session; on a fresh SSH login try 'export XDG_RUNTIME_DIR=/run/user/$(id -u)' first.)` };
  }

  let isActive = '';
  try {
    isActive = execFileSync('systemctl', ['--user', 'is-active', LINUX_UNIT_NAME], { encoding: 'utf-8' }).trim();
  } catch (err) {
    // is-active exits non-zero for inactive/failed states. Capture its
    // output so the operator sees the real status.
    isActive = ((err as { stdout?: Buffer; stderr?: Buffer }).stdout?.toString() ?? '').trim() || 'unknown';
  }

  // Don't claim the install succeeded if the unit ended up failed /
  // inactive / unknown. Without this, the CLI would exit 0 with a
  // green "supervisor installed" message while the manager was
  // already broken.
  if (isActive !== 'active') {
    return {
      ok: false,
      error: `systemd loaded ${LINUX_UNIT_NAME} but it did not become active (status: ${isActive}). Run 'systemctl --user status ${LINUX_UNIT_NAME}' or 'journalctl --user -u ${LINUX_UNIT_NAME} -n 50' for details.`,
    };
  }

  return {
    ok: true,
    details: `Loaded systemd --user unit ${LINUX_UNIT_NAME} (status: ${isActive}). Logs: ${join(opts.configDir, 'manager.log')}.\nFor reboot survival on a headless host run as root: 'loginctl enable-linger ${process.env['USER'] ?? '<your-user>'}'`,
  };
}

function uninstallLinux(): { ok: true; details: string } | { ok: false; error: string } {
  // disable --now stops + un-enables in one shot. Both legs are idempotent
  // — disable on a non-existent unit returns 0 with a notice on stderr.
  bestEffort(() => execFileSync('systemctl', ['--user', 'disable', '--now', LINUX_UNIT_NAME], { stdio: 'ignore' }));

  let removed = false;
  if (existsSync(LINUX_UNIT_PATH)) {
    try {
      unlinkSync(LINUX_UNIT_PATH);
      removed = true;
    } catch (err) {
      return { ok: false, error: `Failed to remove ${LINUX_UNIT_PATH}: ${(err as Error).message}` };
    }
  }
  // Best-effort cleanup of the secrets-only env file too; we don't
  // want a stale AGT_API_KEY left on disk after uninstall.
  if (existsSync(LINUX_ENV_FILE_PATH)) {
    bestEffort(() => unlinkSync(LINUX_ENV_FILE_PATH));
  }

  // Reload so a future install with the same path doesn't reuse the
  // stale unit metadata systemd cached in memory.
  bestEffort(() => execFileSync('systemctl', ['--user', 'daemon-reload'], { stdio: 'ignore' }));

  return {
    ok: true,
    details: removed
      ? `Disabled systemd --user unit ${LINUX_UNIT_NAME} and deleted ${LINUX_UNIT_PATH}.`
      : `${LINUX_UNIT_NAME} was already uninstalled (no unit file at ${LINUX_UNIT_PATH}).`,
  };
}

function statusLinux(): SupervisorPresence {
  if (!existsSync(LINUX_UNIT_PATH)) return { kind: 'not-installed' };
  let pid: number | null = null;
  let activeState = 'unknown';
  try {
    const out = execFileSync(
      'systemctl',
      ['--user', 'show', LINUX_UNIT_NAME, '--property=MainPID,ActiveState'],
      { encoding: 'utf-8' },
    );
    const pidMatch = /^MainPID=(\d+)/m.exec(out);
    if (pidMatch && pidMatch[1] !== '0') pid = Number(pidMatch[1]);
    const stateMatch = /^ActiveState=(\S+)/m.exec(out);
    if (stateMatch) activeState = stateMatch[1] ?? 'unknown';
  } catch (err) {
    // The unit file exists but `systemctl --user` couldn't probe it
    // — most often because XDG_RUNTIME_DIR / the per-user dbus isn't
    // available in this shell. Don't lie to the operator about
    // whether the supervisor is installed; report 'installed,
    // unknown state' instead.
    return {
      kind: 'installed',
      pid: null,
      details: `Unit: ${LINUX_UNIT_PATH} (could not query systemd --user: ${(err as Error).message})`,
    };
  }
  return { kind: 'installed', pid, details: `Unit: ${LINUX_UNIT_PATH} (${activeState})` };
}

// Secrets that go into EnvironmentFile= instead of inline Environment=.
// Anything in this set is written to LINUX_ENV_FILE_PATH (mode 0600)
// and stripped from the unit body. Public env (PATH, AGT_HOST, AGT_TEAM)
// stays inline for visibility in `systemctl show`.
const LINUX_SECRET_ENV_KEYS: ReadonlySet<string> = new Set(['AGT_API_KEY']);

function renderLinuxUnit(opts: InstallOpts): string {
  // Non-secret env stays in the unit body via Environment= lines;
  // secrets are pulled from the EnvironmentFile= written separately.
  // systemd parses Environment= with a peculiar grammar — quotes are
  // optional but values can't contain literal newlines. We trust the
  // env values we forward and quote everything to be safe with spaces.
  const envLines = Object.entries(opts.env)
    .filter(([k, v]) => k.length > 0 && v != null && !LINUX_SECRET_ENV_KEYS.has(k))
    .map(([k, v]) => `Environment="${k}=${escapeForSystemdEnv(v)}"`)
    .join('\n');

  // ExecStart cannot use shell metacharacters; pass the args explicitly
  // via systemd's array-of-args style (newline-separated wouldn't
  // parse — systemd splits on whitespace within ExecStart). So we
  // shell-quote each arg and join.
  const execArgs = [
    opts.agtBin,
    'manager',
    'start',
    '--interval',
    String(opts.intervalSec),
    '--config-dir',
    opts.configDir,
  ]
    .map(shellQuoteForSystemd)
    .join(' ');

  const logPath = join(opts.configDir, 'manager.log');

  return `# agt manager supervisor (ENG-4593)
# Managed by \`agt manager install\` — edits will be overwritten.

[Unit]
Description=Augmented host manager daemon
After=network-online.target
Wants=network-online.target
# Restart rate-limit lives here per systemd.unit(5). Older systemds
# tolerated these directives in the service section but newer ones
# silently ignore them there, leaving the crashloop unprotected.
StartLimitBurst=5
StartLimitIntervalSec=60

[Service]
Type=simple
WorkingDirectory=${opts.configDir}
${envLines}
EnvironmentFile=${LINUX_ENV_FILE_PATH}
ExecStart=${execArgs}

# Always come back from any exit; back off 5s between launches so a
# crashloop can't peg the CPU. The 5/60s window above caps total
# attempts before systemd marks the unit failed.
Restart=always
RestartSec=5

# stdout/stderr → manager.log. journalctl --user -u agt-manager
# still captures everything; this just gives operators a single file
# to tail without learning systemd plumbing.
StandardOutput=append:${logPath}
StandardError=append:${logPath}

[Install]
WantedBy=default.target
`;
}

/**
 * Render the secrets-only env file referenced by EnvironmentFile= in
 * the unit. Format: `KEY=VALUE` per line (systemd's EnvironmentFile
 * parser doesn't support quoting the value the way Environment= does,
 * but it tolerates literal characters as long as the value has no
 * newline). One key per line; trailing newline.
 */
function renderLinuxEnvFile(opts: InstallOpts): string {
  return Object.entries(opts.env)
    .filter(([k, v]) => LINUX_SECRET_ENV_KEYS.has(k) && v != null)
    .map(([k, v]) => `${k}=${v}\n`)
    .join('');
}

function escapeForSystemdEnv(value: string): string {
  // Inside an Environment="K=V" line, " and \\ need escaping. Newlines
  // would terminate the directive — reject them upstream.
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function shellQuoteForSystemd(arg: string): string {
  // systemd's ExecStart parser supports double-quoted strings with
  // backslash escapes, similar to a POSIX shell but no command
  // substitution. Wrap any arg containing whitespace or shell metas.
  if (/^[A-Za-z0-9_./:=-]+$/.test(arg)) return arg;
  return `"${arg.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

// ---------------------------------------------------------------------------
// Linux — systemd system unit (ENG-4706)
// ---------------------------------------------------------------------------
//
// The `--user` unit above survives reboot only if `loginctl enable-linger`
// is set per-user. On headless EC2 hosts that's a footgun: a reboot (kernel
// patch, instance-type change, AWS-initiated maintenance) silently takes
// every agent on the host offline until an operator manually re-launches
// the manager. The system unit lives at /etc/systemd/system/, runs as root,
// and is enabled into multi-user.target — so it comes up automatically at
// boot just like any other service.
//
// Both unit kinds can co-exist; statusLinux() looks at the user one,
// systemUnitStatus() looks at the system one. The CLI surfaces them as
// `agt manager install` (user, dev workflow) and `agt manager
// install-system-unit` (root, host-infra workflow) so the operator
// chooses explicitly.

const LINUX_SYSTEM_UNIT_PATH = '/etc/systemd/system/agt-manager.service';
const LINUX_SYSTEM_ENV_FILE_PATH = '/etc/agt/agt-manager.env';

export interface InstallSystemUnitOpts extends InstallOpts {
  /**
   * Unix user the manager runs as. EC2 default is `root`. Other hosts
   * can run it as a less-privileged user, but the user must own
   * `configDir` and have permission to write to it. Defaults to `root`
   * when omitted (matches the ticket spec).
   */
  user?: string;
}

export async function installSystemUnit(
  opts: InstallSystemUnitOpts,
): Promise<{ ok: true; details: string } | { ok: false; error: string }> {
  if (platform() !== 'linux') {
    return {
      ok: false,
      error: `installSystemUnit is Linux-only (current platform: ${platform()}). For local dev on macOS use installSupervisor.`,
    };
  }
  if (process.getuid?.() !== 0) {
    return {
      ok: false,
      error: 'installSystemUnit requires root. Re-run with sudo, or call from the EC2 user-data bootstrap which already runs as root.',
    };
  }
  if (!existsSync(opts.agtBin)) {
    return { ok: false, error: `agt binary not found at ${opts.agtBin}` };
  }
  if (opts.intervalSec < 5) {
    return { ok: false, error: 'intervalSec must be >= 5' };
  }
  if (!opts.env.AGT_HOST || !opts.env.AGT_API_KEY) {
    return {
      ok: false,
      error: 'AGT_HOST and AGT_API_KEY must be set so the system unit can pass them through. Run `agt setup <token>` first (it writes them to /etc/environment, which this installer reads).',
    };
  }

  // Stop any running instance so daemon-reload picks up the rewritten
  // unit cleanly without a respawn race.
  bestEffort(() => execFileSync('systemctl', ['stop', LINUX_UNIT_NAME], { stdio: 'ignore' }));

  // Belt-and-braces (ENG-4706 backfill): kill any pre-existing manager
  // process that wasn't started via systemd. On hosts that were
  // bootstrapped before this PR, the manager runs nohup'd from a one-
  // shot SSM command. Installing the system unit without stopping that
  // older process leaves TWO managers polling Supabase simultaneously —
  // they race on agent ticks, double-inject channel messages, and
  // double-write audit_log rows. SIGTERM gives it 3s to wind down
  // cleanly before the systemd start grabs the supervisor seat.
  // Ignored on hosts that have no rogue manager (the typical fresh-
  // bootstrap path).
  bestEffort(() => {
    const out = execFileSync('pgrep', ['-f', 'agt manager start'], { stdio: ['ignore', 'pipe', 'ignore'], encoding: 'utf-8' });
    const pids = out
      .split('\n')
      .map((s) => s.trim())
      .filter((s) => s.length > 0 && /^\d+$/.test(s) && Number(s) !== process.pid);
    if (pids.length === 0) return;
    for (const pid of pids) {
      bestEffort(() => process.kill(Number(pid), 'SIGTERM'));
    }
    // Brief settle so the just-killed manager releases its Supabase
    // long-polls before the new one connects. 3s matches the default
    // poll interval; a manager that hasn't shut down in that window
    // gets SIGKILLed below.
    const deadline = Date.now() + 3_000;
    while (Date.now() < deadline) {
      try {
        execFileSync('pgrep', ['-f', 'agt manager start'], { stdio: 'ignore' });
      } catch {
        return;
      }
      // Spin — busy-wait is fine for a 3s ceiling and avoids pulling in
      // a sleep helper. This path runs once at install, never on the
      // hot poll loop.
    }
    // Still alive — escalate.
    for (const pid of pids) {
      bestEffort(() => process.kill(Number(pid), 'SIGKILL'));
    }
  });

  // Secrets-only env file lives under /etc/agt/ (root:root, 0600). Same
  // rationale as the user-unit env file: keep AGT_API_KEY out of any
  // tool that reads systemd's parsed unit cache.
  mkdirSync(dirname(LINUX_SYSTEM_ENV_FILE_PATH), { recursive: true, mode: 0o700 });
  writeFileSync(LINUX_SYSTEM_ENV_FILE_PATH, renderLinuxEnvFile(opts), { mode: 0o600 });
  chmodSync(LINUX_SYSTEM_ENV_FILE_PATH, 0o600);
  writeFileSync(LINUX_SYSTEM_UNIT_PATH, renderLinuxSystemUnit(opts), { mode: 0o644 });
  chmodSync(LINUX_SYSTEM_UNIT_PATH, 0o644);

  try {
    execFileSync('systemctl', ['daemon-reload'], { stdio: 'pipe' });
  } catch (err) {
    return { ok: false, error: `systemctl daemon-reload failed: ${(err as Error).message}` };
  }

  // `enable --now` enables at boot AND starts immediately. Unlike the
  // --user variant, this needs no enable-linger — multi-user.target
  // pulls us in regardless of any user session.
  try {
    execFileSync('systemctl', ['enable', '--now', LINUX_UNIT_NAME], { stdio: 'pipe' });
  } catch (err) {
    return { ok: false, error: `systemctl enable --now failed: ${(err as Error).message}` };
  }

  let isActive = '';
  try {
    isActive = execFileSync('systemctl', ['is-active', LINUX_UNIT_NAME], { encoding: 'utf-8' }).trim();
  } catch (err) {
    isActive = ((err as { stdout?: Buffer; stderr?: Buffer }).stdout?.toString() ?? '').trim() || 'unknown';
  }

  if (isActive !== 'active') {
    return {
      ok: false,
      error: `systemd loaded ${LINUX_UNIT_NAME} but it did not become active (status: ${isActive}). Run 'systemctl status ${LINUX_UNIT_NAME}' or 'journalctl -u ${LINUX_UNIT_NAME} -n 50' for details.`,
    };
  }

  return {
    ok: true,
    details: `Loaded systemd system unit ${LINUX_UNIT_NAME} at ${LINUX_SYSTEM_UNIT_PATH} (status: ${isActive}). Will auto-start on reboot via multi-user.target. Logs: ${join(opts.configDir, 'manager.log')} and 'journalctl -u ${LINUX_UNIT_NAME}'.`,
  };
}

export async function uninstallSystemUnit(): Promise<{ ok: true; details: string } | { ok: false; error: string }> {
  if (platform() !== 'linux') {
    return { ok: false, error: `uninstallSystemUnit is Linux-only (current platform: ${platform()}).` };
  }
  if (process.getuid?.() !== 0) {
    return { ok: false, error: 'uninstallSystemUnit requires root.' };
  }

  bestEffort(() => execFileSync('systemctl', ['disable', '--now', LINUX_UNIT_NAME], { stdio: 'ignore' }));

  let removed = false;
  if (existsSync(LINUX_SYSTEM_UNIT_PATH)) {
    try {
      unlinkSync(LINUX_SYSTEM_UNIT_PATH);
      removed = true;
    } catch (err) {
      return { ok: false, error: `Failed to remove ${LINUX_SYSTEM_UNIT_PATH}: ${(err as Error).message}` };
    }
  }
  if (existsSync(LINUX_SYSTEM_ENV_FILE_PATH)) {
    bestEffort(() => unlinkSync(LINUX_SYSTEM_ENV_FILE_PATH));
  }

  bestEffort(() => execFileSync('systemctl', ['daemon-reload'], { stdio: 'ignore' }));

  return {
    ok: true,
    details: removed
      ? `Disabled systemd system unit ${LINUX_UNIT_NAME} and deleted ${LINUX_SYSTEM_UNIT_PATH}.`
      : `${LINUX_UNIT_NAME} system unit was already uninstalled (no unit file at ${LINUX_SYSTEM_UNIT_PATH}).`,
  };
}

export function systemUnitStatus(): SupervisorPresence {
  if (platform() !== 'linux') {
    return { kind: 'unsupported-platform', platform: platform() };
  }
  if (!existsSync(LINUX_SYSTEM_UNIT_PATH)) return { kind: 'not-installed' };
  let pid: number | null = null;
  let activeState = 'unknown';
  try {
    const out = execFileSync(
      'systemctl',
      ['show', LINUX_UNIT_NAME, '--property=MainPID,ActiveState'],
      { encoding: 'utf-8' },
    );
    const pidMatch = /^MainPID=(\d+)/m.exec(out);
    if (pidMatch && pidMatch[1] !== '0') pid = Number(pidMatch[1]);
    const stateMatch = /^ActiveState=(\S+)/m.exec(out);
    if (stateMatch) activeState = stateMatch[1] ?? 'unknown';
  } catch (err) {
    return {
      kind: 'installed',
      pid: null,
      details: `Unit: ${LINUX_SYSTEM_UNIT_PATH} (could not query systemd: ${(err as Error).message})`,
    };
  }
  return { kind: 'installed', pid, details: `Unit: ${LINUX_SYSTEM_UNIT_PATH} (${activeState})` };
}

/**
 * ENG-6698: ExecStartPre self-heal for a missing/dangling `agt` binary.
 *
 * The incident (agt-aws-1, full-fleet outage): a CLI self-update killed
 * mid-`npm i -g` (npm removes the old package before writing the new) left
 * `/usr/bin/agt` a dangling symlink, so `ExecStart` failed with 203/EXEC and
 * systemd crash-looped the unit to `failed`. With no recovery path the whole
 * fleet went dark until an operator manually reinstalled over SSM.
 *
 * This guard runs before every `ExecStart`: if `agt --version` won't run, it
 * reinstalls the SAME release channel (read from the unit's
 * AGT_CLI_RELEASE_CHANNEL env, default `latest`) before the manager starts, so
 * a dangling binary self-heals on the next start attempt instead of latching
 * to `failed` with no way back.
 *
 * Two escaping notes that are load-bearing:
 *  - systemd does its OWN `${VAR}` expansion on Exec lines (and does not
 *    support the POSIX `:-default` form), so every shell `$` is written as
 *    `$$` to pass through verbatim to /bin/sh, which then does the
 *    `${VAR:-latest}` expansion itself.
 *  - the script is one single-quoted `/bin/sh -c` argument with no single
 *    quotes inside; `agtBin` is a filesystem path (no single quotes on our
 *    hosts) and is double-quoted so a stray space can't split the arg.
 */
export function buildSystemUnitRepairExecStartPre(agtBin: string, user: string): string {
  const sudo = user === 'root' ? '' : 'sudo -n ';
  // Single-quoted JS strings so the literal `$${...}` reaches the unit file
  // untouched (a template literal would try to interpolate `${...}`). The bare
  // expansion is embedded inside the echo's own double-quotes; the npm arg gets
  // its own double-quotes — NOT nested, or the inner quotes would break the
  // outer string and word-split the package spec.
  const channelExpansion = '$${AGT_CLI_RELEASE_CHANNEL:-latest}';
  const installSpec = '"@integrity-labs/agt-cli@' + channelExpansion + '"';
  const script =
    '"' + agtBin + '" --version >/dev/null 2>&1 || ' +
    '{ echo "[agt-manager] agt binary missing/dangling - reinstalling @integrity-labs/agt-cli@' +
    channelExpansion +
    '"; ' +
    sudo +
    'npm install -g ' +
    installSpec +
    ' --registry=https://registry.npmjs.org; }';
  return "ExecStartPre=/bin/sh -c '" + script + "'";
}

function renderLinuxSystemUnit(opts: InstallSystemUnitOpts): string {
  const user = opts.user ?? 'root';
  const home = user === 'root' ? '/root' : `/home/${user}`;

  // Non-secret env stays in the unit body via Environment= lines;
  // secrets are pulled from EnvironmentFile= written separately.
  const envLines = Object.entries(opts.env)
    .filter(([k, v]) => k.length > 0 && v != null && !LINUX_SECRET_ENV_KEYS.has(k))
    .map(([k, v]) => `Environment="${k}=${escapeForSystemdEnv(v)}"`)
    .join('\n');

  const execArgs = [
    opts.agtBin,
    'manager',
    'start',
    '--interval',
    String(opts.intervalSec),
    '--config-dir',
    opts.configDir,
  ]
    .map(shellQuoteForSystemd)
    .join(' ');

  const logPath = join(opts.configDir, 'manager.log');

  return `# agt manager supervisor — system unit (ENG-4706)
# Managed by \`agt manager install-system-unit\` — edits will be overwritten.
#
# Unlike the sibling --user unit at ~/.config/systemd/user/agt-manager.service,
# this one is enabled into multi-user.target so it auto-starts on every
# boot regardless of any user login. Use this on EC2 / headless hosts.

[Unit]
Description=Augmented host manager daemon
After=network-online.target
Wants=network-online.target
# ENG-6698: widened from 5/60s. The ExecStartPre repair below reinstalls a
# dangling agt binary on each start attempt (~7s of npm + RestartSec), so a
# transient miss needs several attempts within the window to self-heal before
# systemd would otherwise latch the unit to \`failed\`. 10 tries / 5 min gives
# the repair room to recover without spinning forever on a genuinely broken host.
StartLimitBurst=10
StartLimitIntervalSec=300

[Service]
Type=simple
User=${user}
WorkingDirectory=${opts.configDir}
Environment="HOME=${home}"
${envLines}
EnvironmentFile=${LINUX_SYSTEM_ENV_FILE_PATH}
# ENG-6698: self-heal a missing/dangling agt binary before starting, so an
# interrupted self-update can't latch the unit to \`failed\` with no recovery.
${buildSystemUnitRepairExecStartPre(opts.agtBin, user)}
ExecStart=${execArgs}

# on-failure (not always) so a clean \`systemctl stop\` doesn't fight a
# respawn loop. The ExecStartPre repair + the widened StartLimit above turn a
# dangling-binary crashloop into a self-healing one. Logs land in ${logPath}
# via the manager's own writer plus journalctl -u ${LINUX_UNIT_NAME}.
Restart=on-failure
RestartSec=5

StandardOutput=append:${logPath}
StandardError=append:${logPath}

[Install]
WantedBy=multi-user.target
`;
}

// Exposed for unit tests / introspection.
export const _internals = {
  MACOS_LABEL,
  MACOS_PLIST,
  LINUX_UNIT_NAME,
  LINUX_UNIT_PATH,
  LINUX_ENV_FILE_PATH,
  LINUX_SYSTEM_UNIT_PATH,
  LINUX_SYSTEM_ENV_FILE_PATH,
  LINUX_SECRET_ENV_KEYS,
  renderMacosPlist,
  renderLinuxUnit,
  renderLinuxSystemUnit,
  renderLinuxEnvFile,
  escapeXml,
  escapeForSystemdEnv,
  shellQuoteForSystemd,
  readPlist: () => (existsSync(MACOS_PLIST) ? readFileSync(MACOS_PLIST, 'utf-8') : null),
  readUnit: () => (existsSync(LINUX_UNIT_PATH) ? readFileSync(LINUX_UNIT_PATH, 'utf-8') : null),
  readSystemUnit: () => (existsSync(LINUX_SYSTEM_UNIT_PATH) ? readFileSync(LINUX_SYSTEM_UNIT_PATH, 'utf-8') : null),
  readEnvFile: () => (existsSync(LINUX_ENV_FILE_PATH) ? readFileSync(LINUX_ENV_FILE_PATH, 'utf-8') : null),
  readSystemEnvFile: () => (existsSync(LINUX_SYSTEM_ENV_FILE_PATH) ? readFileSync(LINUX_SYSTEM_ENV_FILE_PATH, 'utf-8') : null),
};
